import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../theme";
import { fetchDataEngine } from "../../../data/Axios/DynamicService";
import { HRService } from "../../../config";

const base_url = HRService.uri;

/**
 * ManageRelieversDialog - View-only dialog for leave application relievers
 * Uses consistent UI with DynamicLinesDialog but is read-only
 */
const ManageRelieversDialog = ({ open, onClose, leaveApplicationData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [relievers, setRelievers] = useState([]);

  const leaveApplicationId = leaveApplicationData?.id;

  // Table columns
  const columns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    { field: "leaveType", headerName: "Leave Type", flex: 1 },
    { field: "statusDescription", headerName: "Status", flex: 1 },
    { field: "applicantFirstName", headerName: "Applicant First Name", flex: 1 },
    { field: "applicantLastName", headerName: "Applicant Last Name", flex: 1 },
    { field: "relieverFirstName", headerName: "Reliever First Name", flex: 1 },
    { field: "relieverLastName", headerName: "Reliever Last Name", flex: 1 },
    { field: "role", headerName: "Role", flex: 1 },
  ];

  const fetchRelievers = async () => {
    if (!leaveApplicationId) return;

    setLoading(true);
    try {
      const result = await fetchDataEngine(
        "relieversByApplicationId",
        "reliever",
        { leaveApplicationId: Number(leaveApplicationId) },
        [
          "id",
          "leaveType",
          "statusDescription",
          "applicantFirstName",
          "applicantLastName",
          "relieverFirstName",
          "relieverLastName",
          "role",
        ],
        base_url
      );
      setRelievers(result || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && leaveApplicationId) {
      fetchRelievers();
    }
  }, [open, leaveApplicationId]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: colors.blueAccent[700],
          color: colors.grey[100],
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        Manage Relievers
        <IconButton onClick={handleClose} sx={{ color: colors.grey[100] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: colors.primary[400] }}>
        <Typography variant="body2" color={colors.grey[300]} mb={2}>
          View relievers for this leave application
        </Typography>

        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: colors.greenAccent[500] }} />
          </Box>
        ) : relievers.length > 0 ? (
          <DataGrid
            rows={relievers}
            columns={columns}
            autoHeight
            pageSize={5}
            getRowId={(row) => row.id}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: colors.primary[400],
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: colors.blueAccent[700],
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${colors.grey[700]}`,
              },
            }}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography color="textSecondary">
              No relievers found.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageRelieversDialog;
